<script setup lang="ts">
import type {HTMLAttributes} from "vue"
import {computed, nextTick, onMounted, onUpdated, ref} from "vue"
import {useEventListener, useResizeObserver} from "@vueuse/core"
import {ScrollAreaRoot, ScrollAreaViewport} from "reka-ui"
import {ScrollBar} from "~/components/ui/scroll-area"
import {cn} from "@/lib/utils"

// A vertically scrolling box (the app's own ScrollArea scrollbar, not the system one) that fades
// its top/bottom edge out whenever there's more content past that edge. The fade is a mask on the
// content itself, not a background-coloured overlay, so whatever is behind the box (a tinted glow,
// a muted card, either theme) shows through unchanged — an overlay painted its own band of
// `--background` over it.
//
// With `expandedViewportClass` set, scrolling it pops the box out of the layout: it becomes a
// floating panel over whatever is below, with that taller height cap, while an empty placeholder
// of the collapsed height keeps the surrounding layout exactly where it was. It folds back once
// the pointer is outside the panel.
const props = defineProps<{
  /** Classes for the content wrapper (layout, gaps…). */
  class?: HTMLAttributes["class"]
  /** Classes for the scrolling viewport — put the height cap here, e.g. `max-h-28`. */
  viewportClass?: HTMLAttributes["class"]
  /** Classes for the outer box, e.g. `min-h-0 flex-1` to fill a flex column; the viewport then
   *  takes `h-full` to scroll within that height. */
  rootClass?: HTMLAttributes["class"]
  /** Height cap while popped out on scroll, e.g. `max-h-60`. Leave unset to never pop out. */
  expandedViewportClass?: HTMLAttributes["class"]
  /** Extra classes for the popped-out panel, e.g. a bigger negative `right` to widen it. */
  expandedClass?: HTMLAttributes["class"]
}>()

// The panel that floats when popped out. A plain div around ScrollAreaRoot rather than the root
// itself: reka puts an inline `position: relative` on ScrollAreaRoot, which beat the `absolute`
// class — the box never actually left the layout, it only shifted by its negative offsets.
const panel = ref<HTMLElement | null>(null)
const viewport = ref<InstanceType<typeof ScrollAreaViewport> | null>(null)
const rootElement = computed<HTMLElement | null>(() => panel.value)
const viewportElement = computed<HTMLElement | null>(() => viewport.value?.viewportElement ?? null)
const canScrollUp = ref(false)
const canScrollDown = ref(false)

const FADE = "20px"
const fadeMaskStyle = computed(() => {
  if (!canScrollUp.value && !canScrollDown.value) return undefined
  const top = canScrollUp.value ? `transparent 0, black ${FADE}` : "black 0"
  const bottom = canScrollDown.value ? `black calc(100% - ${FADE}), transparent 100%` : "black 100%"
  const mask = `linear-gradient(to bottom, ${top}, ${bottom})`
  return {maskImage: mask, WebkitMaskImage: mask}
})

const expandable = computed(() => !!props.expandedViewportClass)
const expanded = ref(false)
// The in-flow height the placeholder holds on to while the panel floats. Only measured while
// collapsed — the floating panel is taller and mustn't feed back into it.
const collapsedHeight = ref<number | null>(null)

function update() {
  const el = viewportElement.value
  if (!el) return
  // 1px tolerance: fractional scroll positions on zoomed/HiDPI screens never quite hit the end.
  canScrollUp.value = el.scrollTop > 1
  canScrollDown.value = el.scrollTop + el.clientHeight < el.scrollHeight - 1
}

function measureCollapsed() {
  if (!expandable.value || expanded.value || !rootElement.value) return
  collapsedHeight.value = rootElement.value.offsetHeight
}

// Last known pointer position, tracked document-wide. `pointerleave` alone missed exits: it
// doesn't fire when the panel shrinks out from under a still pointer, after a scrollbar drag
// (pointer capture) released outside, or when momentum scrolling expands the panel after the
// pointer has already gone.
let lastPointer: {x: number; y: number} | null = null

function isPointerInside() {
  const el = rootElement.value
  if (!el || !lastPointer) return false
  const rect = el.getBoundingClientRect()
  return lastPointer.x >= rect.left && lastPointer.x <= rect.right
      && lastPointer.y >= rect.top && lastPointer.y <= rect.bottom
}

function handleScroll() {
  update()
  // Only a scroll the pointer is driving pops the panel out — not keyboard/programmatic scrolls,
  // nor momentum still running after the pointer left.
  if (expandable.value && !expanded.value && isPointerInside()) {
    measureCollapsed()
    expanded.value = true
  }
}

function collapse() {
  if (!expanded.value) return
  expanded.value = false
  nextTick(() => {
    measureCollapsed()
    update()
  })
}

useEventListener(document, "pointermove", (event: PointerEvent) => {
  lastPointer = {x: event.clientX, y: event.clientY}
  if (expanded.value && !isPointerInside()) collapse()
}, {passive: true})
// A wheel scroll can come before any pointermove (the pointer hasn't moved since the dialog
// opened), so it reports the position too — capture phase, ahead of the scroll it causes.
useEventListener(document, "wheel", (event: WheelEvent) => {
  lastPointer = {x: event.clientX, y: event.clientY}
}, {passive: true, capture: true})
// Leaving the window entirely (or switching apps) never reports a pointer position outside.
useEventListener(document.documentElement, "pointerleave", () => {
  lastPointer = null
  collapse()
})
useEventListener(window, "blur", collapse)

onMounted(() => {
  update()
  measureCollapsed()
})
// Content changes (items added/removed through the slot) re-render this component too.
onUpdated(() => nextTick(() => {
  update()
  measureCollapsed()
}))
useResizeObserver(viewportElement, update)
useResizeObserver(rootElement, measureCollapsed)
</script>

<template>
  <div :class="cn('relative', rootClass)" :style="expanded && collapsedHeight !== null ? {height: `${collapsedHeight}px`} : undefined">
    <!-- Popped out, the panel grows its padding outward (negative inset) so the content itself
         doesn't shift. -->
    <div
        ref="panel"
        :class="expanded
          ? cn('-left-1.5 -top-1.5 absolute z-20 rounded-xl bg-background p-1.5 shadow-lg', expandedClass ?? '-right-1.5')
          : 'h-full'"
    >
      <!-- `type="hover"`: the scrollbar shows while the pointer is over the box and hides once it
           leaves; the edge fades keep signalling "there's more" either way. -->
      <ScrollAreaRoot type="hover" class="h-full overflow-hidden">
        <ScrollAreaViewport
            ref="viewport"
            :class="cn('w-full outline-none transition-[max-height] duration-200', expanded ? expandedViewportClass : viewportClass)"
            :style="fadeMaskStyle"
            @scroll.passive="handleScroll"
        >
          <div :class="props.class">
            <slot/>
          </div>
        </ScrollAreaViewport>
        <ScrollBar/>
      </ScrollAreaRoot>
    </div>
  </div>
</template>
